<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main.vue'
import { store } from '../../state/store'
import Header from '@components/header.vue'
import ZoneService from '@services/business/zone.service'
import EventHistoryService from '@services/business/event-history.service'
import FooterComponent from '@components/footer.vue'
import router from '../index'
import ErrorAlert from '@components/error-alert.vue'
import WarningAlert from '@components/warning-alert.vue'
import { setTimeout } from 'timers'
import TableBody from '@components/scrolling-table-body.vue'
import moment from 'moment'
import { phoneNumbers } from '../../models/enums'

export default {
  page: {
    title: 'Signal Test',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    Header,
    FooterComponent,
    ErrorAlert,
    WarningAlert,
    TableBody,
  },
  data() {
    return {
      state: store.state,
      timeoutContext: null,
      isListenerActive: true,
      columns: {
        Date: {
          value: 'Date',
          style: 'width: 5em',
        },
        Zone: {
          value: 'Zone',
          style: 'width: 5em',
        },
        Event: {
          value: 'Event',
          style: '',
        },
        Comment: {
          value: 'Comment',
          style: '',
        },
      },
      rows: [],
      isLoading: false,
      warningAlertMessage: '',
      GENERAL_PHONE_NUMBER: phoneNumbers.GENERAL,
    }
  },
  mounted() {
    this.getSignalTestData()
  },
  beforeDestroy() {
    this.isListenerActive = false
  },
  methods: {
    goToCompletePage() {
      router.push('job-complete')
    },
    goBack() {
      router.back()
    },
    getZones() {
      return ZoneService.getZonesForSite().catch(() => {
        store.presentErrorAlert('Unable to retrieve zones for this site.')
      })
    },
    getEventHistory() {
      return EventHistoryService.getEventHistoriesForSignalTest()
        .then(() => {
          if (!this.state.systemTest.onTest) {
            this.warningAlertMessage = 'The system is not on test.'
          }
          this.createEventHistoryRows()
        })
        .catch((err) => {
          console.error(err)
          store.presentErrorAlert(
            'Unable to retrieve event histories for this site.'
          )
        })
        .finally(() => {
          if (this.isListenerActive) {
            setTimeout(this.getEventHistory, 5000)
          }
        })
    },
    async getSignalTestData() {
      this.isLoading = true
      await this.getZones()
      this.getEventHistory()
      this.isLoading = false
    },
    createEventHistoryRows() {
      this.rows = []
      this.state.signalTestEventHistory.forEach((event) => {
        let row = {
          Date: {
            value: moment(new Date(event.eventDate)).format('MM/DD/YY hh:mm'),
            style: 'width: 5em',
          },
          Zone: {
            value: event.zoneId,
            style: 'width: 5em',
          },
          Event: {
            value: event.eventDescription,
            style: '',
          },
          Comment: {
            value: event.comment,
            style: '',
          },
        }
        this.rows.push(row)
      })
    },
  },
}
</script>

<template>
  <Layout>
    <Header />
    <div :class="$style.signal_test_parent_container">
      <div v-show="isLoading" :class="$style.spinner_container">
        <div class="spinner"></div>
      </div>
      <div v-show="!isLoading" :class="$style.signal_test_container">
        <h1 :class="$style.signal_test_title">SIGNAL TEST</h1>
        <ErrorAlert />
        <WarningAlert :message="warningAlertMessage" />
        <span :class="$style.refresh_button_container">
          <button :class="$style.refresh_button" @click="getSignalTestData">
            <img
              :class="$style.refresh_image"
              src="@src/assets/images/Refresh_icon.svg#refresh"
            />
          </button>
        </span>
        <div :class="$style.zone_container">
          <h3 :class="$style.signal_test_title">Zones</h3>
          <div
            v-for="zone in state.zones"
            :key="'zone' + zone.zoneId"
            class="row"
            :class="$style.zone_row"
          >
            <div :class="$style.text_primary" class="col col-3">
              {{ zone.zoneId }}
            </div>
            <div class="col col-3">
              {{ zone.location }}
            </div>
            <div class="col col-3">
              {{ zone.equipmentType }}
            </div>
            <div class="col col-3">
              <svg
                v-if="zone.signalReceived"
                class="btn__icon btn__icon--right icon icon-circle-check"
                :class="$style.received_signal_icon"
              >
                <use
                  xlink:href="../../assets/images/brinkshome_icons.svg#icon-circle-check"
                ></use>
              </svg>
              <svg
                v-else
                class="btn__icon btn__icon--right icon icon-circle-x"
                :class="$style.unreceived_signal_icon"
              >
                <use
                  xlink:href="../../assets/images/brinkshome_icons.svg#icon-circle-x"
                ></use>
              </svg>
            </div>
          </div>
        </div>
        <div :class="$style.zones_help_message">
          If you need to add zones, call
          <a
            id="zones-help-phone"
            :class="$style.phone_link"
            :href="'tel:' + GENERAL_PHONE_NUMBER"
            >{{ GENERAL_PHONE_NUMBER }}</a
          >
        </div>
        <div :class="$style.event_history_table_container">
          <h3 :class="$style.signal_test_title">Events</h3>
          <table :class="$style.fixed_table_header">
            <thead>
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.value"
                  :value="column.value"
                  :style="column.style"
                >
                  {{ column.value }}
                </th>
              </tr>
            </thead>
          </table>
          <TableBody :rows="rows"></TableBody>
        </div>
      </div>
    </div>
    <FooterComponent
      primary-btn-text="COMPLETE"
      :primary-btn-callback="goToCompletePage"
      primary-btn-id="btn-job-complete-redirect"
    ></FooterComponent>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.signal_test_container {
  width: 100%;
  padding: 1em;
}

.signal_test_parent_container {
  position: relative;
  top: $height-status-bar;
  width: 100%;
  margin-bottom: $height-footer;
}

.signal_test_title {
  margin: 0;
  color: $color-text-primary;
}

.text_primary {
  font-weight: bold;
  color: $color-text-primary;
}

.zone_container {
  width: 100%;
}

.zone_row {
  width: 100%;
  padding: 1em;
  margin: auto;
  margin-bottom: 1em;
  border: 3px solid $color-text-primary;
}

.received_signal_icon {
  font-size: 1.5em;
  color: $color-teal;
}

.unreceived_signal_icon {
  font-size: 1.5em;
  color: $color-unreceived-signal;
}

.refresh_button {
  float: right;
  background-color: transparent;
  border: 0;
  color: $color-text-primary;
  cursor: pointer;
  font-size: 1em;
  padding: 0;
}

.refresh_image {
  width: 2em;
}

.zones_help_message {
  text-align: center;
  color: $color-text-secondary;
  width: 85%;
  margin: auto;
}

.phone_link {
  color: $color-text-primary;
  text-decoration: underline;
  font-weight: bold;
}

.event_history_table_container {
  position: relative;
  top: $height-navbar;
  padding-bottom: $height-footer;
}

.fixed_table_header {
  position: sticky;
  top: $height-status-bar + $height-navbar;
  z-index: 3;
  width: 100%;
  height: $height-table-header;
  color: $color-text-primary;
  text-align: center;
  table-layout: fixed;
  border-spacing: 0.25em;
  border-collapse: separate;
  background: $color-body-bg;
}

.table_column {
  word-wrap: break-word;
}

.spinner_container {
  position: fixed;
  top: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>
